/* eslint-disable max-len */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CategoryCustomProduct,
  CategoryDetails,
  CategoryTotalResponse,
  ClientLayoutTypeCategoryData,
  ClientPackageDataModel,
  ClientPackageModel,
  EOptionRuleItemsKind,
  ERuleKind,
  ExpiredCategories,
  IClientLayoutTypeCategory,
  ISettingsMenu,
  LayoutProductType,
  LayoutProductTypeResponse,
  LayoutProductTypesResponse,
  OptionCategoryProduct,
  ProductListItemModelCPB,
  ProductModel,
  ResFreeTextProductModel,
  RuleDependableProduct,
  WishlistModel,
} from '@atlas-workspace/shared/models';
import { environment } from '@environment-client';
import { plainToClass } from 'class-transformer';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class OptionsService {
  private categoriesMenu$ = new BehaviorSubject<ISettingsMenu[]>([]);
  private _packages$ = new BehaviorSubject<ClientPackageDataModel | null>(null);
  private _wishlistHasCompletedItem$ = new BehaviorSubject<boolean>(false);
  private _showRoomCompleted$ = new BehaviorSubject<boolean | null>(null);
  private _changeProductHandler = new Subject<boolean | void>();
  private _categoryData$ = new BehaviorSubject<ClientLayoutTypeCategoryData | null>(null);
  private readonly apiUrl = environment.apiBaseUrl;

  private _hideNoStandardDeliveryPopup$ = new BehaviorSubject<boolean>(false);
  private _categoryTotalPrice$ = new BehaviorSubject<number>(0);
  private _standardProductPreselection$ = new BehaviorSubject<boolean>(false);

  private _categoryCustomProducts$ = new BehaviorSubject<CategoryCustomProduct[]>([]);
  constructor(private http: HttpClient) { }

  public get hideNoStandardDeliveryPopup(): Observable<boolean> {
    return this._hideNoStandardDeliveryPopup$.asObservable();
  }

  public setHideNoStandardDeliveryPopup(value: boolean): void {
    this._hideNoStandardDeliveryPopup$.next(value);
  }

  public get categoryTotalPrice(): Observable<number> {
    return this._categoryTotalPrice$.asObservable();
  }

  public setCategoryTotalPrice(value: number): void {
    this._categoryTotalPrice$.next(value);
  }

  public setCategoryCustomProducts(value: CategoryCustomProduct[]): void {
    this._categoryCustomProducts$.next(value);
  }

  public get categoryCustomProducts$(): Observable<CategoryCustomProduct[]> {
    return this._categoryCustomProducts$.asObservable();
  }

  public get standardProductPreselection(): Observable<boolean> {
    return this._standardProductPreselection$.asObservable();
  }

  public setStandardProductPreselection(value: boolean): void {
    this._standardProductPreselection$.next(value);
  }

  public get categoriesMenu(): Observable<ISettingsMenu[]> {
    return this.categoriesMenu$.asObservable();
  }

  public get categoryData$(): Observable<ClientLayoutTypeCategoryData | null> {
    return this._categoryData$.asObservable();
  }

  public setCategoryData(value: ClientLayoutTypeCategoryData): void {
    this._categoryData$.next(value);
  }

  public get packages$(): Observable<ClientPackageDataModel | null> {
    return this._packages$.asObservable();
  }

  public setPackages(packages: ClientPackageDataModel): void {
    this._packages$.next(packages);
  }

  public get wishlistHasCompletedItem$(): Observable<boolean> {
    return this._wishlistHasCompletedItem$.asObservable();
  }

  public setWishlistCompletedItem(value: boolean): void {
    this._wishlistHasCompletedItem$.next(value);
  }

  public setCompletedShowroom(value: boolean): void {
    this._showRoomCompleted$.next(value);
  }

  public get showRoomCompleted$(): Observable<boolean | null> {
    return this._showRoomCompleted$.asObservable();
  }

  public setCategoriesMenu(menu: ISettingsMenu[]): void {
    this.categoriesMenu$.next(menu);
  }

  get changeProductHandler$(): Subject<boolean | void> {
    return this._changeProductHandler;
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Client-Units/paths/~1api~1v1~1client~1projects~1{project_id}~1units~1{id}~1hide_no_standard_delivery_popup/post
   */
  getPastDueProducts(projectId: number, unitId: number, layoutTypeId: number): Observable<ExpiredCategories> {
    let params: HttpParams = new HttpParams();
    params = params.set('layout_type_id', layoutTypeId.toString()).set('unit_id', String(unitId));
    return this.http
      .get<{
        data: { layout_option_categories: IClientLayoutTypeCategory[] };
      }>(
        `${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_categories/expired_products`,
        { params },
      )
      .pipe(
        map((res) => res.data),
        map((option) => plainToClass(ExpiredCategories, option)),
      );
  }

  public getLayoutOptionsCategories(
    projectId: number,
    layoutTypeId: number,
    unitId: number,
  ): Observable<ClientLayoutTypeCategoryData> {
    let params: HttpParams = new HttpParams();
    params = params.set('layout_type_id', layoutTypeId.toString()).set('unit_id', String(unitId));
    return this.http
      .get<{
        data: { layout_option_categories: IClientLayoutTypeCategory[] };
      }>(`${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_categories`, { params })
      .pipe(
        map((res) => res.data),
        map((option) => plainToClass(ClientLayoutTypeCategoryData, option)),
      );
  }

  public resetCategory(
    projectId: number,
    typeId: number,
    categoryId: number,
    unitId: number,
  ): Observable<LayoutProductType[]> {
    const params: HttpParams = new HttpParams().set('unit_id', String(unitId));
    return this.http
      .post<LayoutProductTypesResponse>(
        `${this.apiUrl}api/v1/projects/${projectId}/layout_types/${typeId}/layout_option_categories/${categoryId}/reset_products`,
        null,
        { params },
      )
      .pipe(
        map((res) => res.data.layout_product_types),
        map((layoutProductTypes: LayoutProductType[]) => plainToClass(LayoutProductType, layoutProductTypes)),
      );
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Client-Layout-Option-Categories/paths/~1api~1v1~1projects~1{project_id}~1layout_types~1{layout_type_id}~1layout_option_categories~1{id}~1total_price/get
   */
  public getCategoryTotal(
    projectId: number,
    layoutTypeId: number,
    categoryId: number,
    unitId: number,
  ): Observable<CategoryTotalResponse> {
    const params: HttpParams = new HttpParams().set('unit_id', String(unitId));
    return this.http
      .get<CategoryTotalResponse>(
        `${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_categories/${categoryId}/total_price`,
        { params },
      )
      .pipe(
        map((res: any) => {
          this.setCategoryTotalPrice(res.data.total_price);
          return res.data;
        }),
        map((data) => plainToClass(CategoryTotalResponse, data)),
      );
  }

  public getLayoutOptionCategory(
    projectId: number,
    layoutTypeId: number,
    categoryId: number,
    unitId: number,
  ): Observable<CategoryDetails> {
    const params: HttpParams = new HttpParams().set('unit_id', String(unitId));
    return this.http
      .get<CategoryDetails>(
        `${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_categories/${categoryId}`,
        { params },
      )
      .pipe(
        map((res: any) => res.data),
        map((data) => plainToClass(CategoryDetails, data)),
      );
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Client-Units/paths/~1api~1v1~1client~1projects~1{project_id}~1units~1{id}~1hide_standard_info/post 
   */
  public hideClientNoStandardDeliveryPopup(projectId: number, unitId: number): Observable<string> {
    return this.http
      .post(`${this.apiUrl}api/v1/client/projects/${projectId}/units/${unitId}/hide_standard_info`, null)
      .pipe(map((res: any) => <string>res?.message));
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Client-Wishlist-Items
   */
  public getCustomProductDetails(projectId: number, unitId: number, wishlistItemId: number): Observable<ProductModel> {
    return this.http
      .get<any>(
        `${this.apiUrl}api/v1/client/projects/${projectId}/units/${unitId}/wishlist_items/${wishlistItemId}`)
      .pipe(
        map((res) => res.data),
        map((product) => plainToClass(ProductModel, product)),
      )
  }

  public getProductTypes(
    projectId: number,
    layoutTypeId: number,
    categoryId: number,
    unitId: number,
    maxCount?: number,
  ): Observable<LayoutProductType[]> {
    let params: HttpParams = new HttpParams().set('unit_id', String(unitId)).append('only_image_versions', 'w260');
    if (maxCount) {
      params = params.append('max_products_count', maxCount).append('max_groups_count', maxCount);
    }
    return this.http
      .get<LayoutProductTypesResponse>(
        `${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_categories/${categoryId}/layout_product_types`,
        { params },
      )
      .pipe(
        map((res) => {
          this.setHideNoStandardDeliveryPopup(res.data.hide_standard_info);
          this.setCategoryTotalPrice(res.data.total_price);
          this.setStandardProductPreselection(res.data.standard_product_preselection);
          const customProducts = plainToClass(CategoryCustomProduct, res.data.custom_products as CategoryCustomProduct[]);
          this.setCategoryCustomProducts(customProducts);
          return res.data.layout_product_types;
        }),
        map((layoutProductTypes: LayoutProductType[]) => plainToClass(LayoutProductType, layoutProductTypes)),
        map((types: LayoutProductType[]) => {
          types.forEach((type) => {
            if (!type.grouped) {
              type.products?.forEach((product) => {
                product.exclusionProducts =
                  product.dependableProducts?.filter((x) => x.ruleKind === ERuleKind.Exclusion) || [];
                product.forcingProducts =
                  product.dependableProducts?.filter((x) => x.ruleKind === ERuleKind.Forcing) || [];
              });

              type.increaseProducts = this.getUniqueDependableProducts(
                type.products,
                ERuleKind.Increasing
              );

              type.decreaseProducts = this.getUniqueDependableProducts(
                type.products,
                ERuleKind.Decreasing
              );


              type.increaseTriggerProducts = this.getUniqueDependableProducts(
                type.products,
                ERuleKind.Increasing,
                EOptionRuleItemsKind.Triggerable
              );

              type.decreaseTriggerProducts = this.getUniqueDependableProducts(
                type.products,
                ERuleKind.Decreasing,
                EOptionRuleItemsKind.Triggerable
              );
            } else {
              type.groups?.forEach((group) => {
                type.increaseProducts = this.getUniqueDependableProducts(
                  group.products,
                  ERuleKind.Increasing
                );

                type.decreaseProducts = this.getUniqueDependableProducts(
                  group.products,
                  ERuleKind.Decreasing
                );


                type.increaseTriggerProducts = this.getUniqueDependableProducts(
                  group.products,
                  ERuleKind.Increasing,
                  EOptionRuleItemsKind.Triggerable
                );

                type.decreaseTriggerProducts = this.getUniqueDependableProducts(
                  group.products,
                  ERuleKind.Decreasing,
                  EOptionRuleItemsKind.Triggerable
                );

                group.products?.forEach((product) => {
                  product.exclusionProducts =
                    product.dependableProducts?.filter((x) => x.ruleKind === ERuleKind.Exclusion) || [];
                  product.forcingProducts =
                    product.dependableProducts?.filter((x) => x.ruleKind === ERuleKind.Forcing) || [];
                });
              });
            }
          });
          return types;
        }),
      );
  }

  private getUniqueDependableProducts(products: ProductListItemModelCPB[], ruleKind: ERuleKind, kind = EOptionRuleItemsKind.Dependable): RuleDependableProduct[] {
    return products
      .flatMap((product) => product.dependableProducts || [])
      .filter(
        (dependable) =>
          dependable.ruleKind === ruleKind &&
          dependable.kind === kind
      )
      .filter(
        (item, index, self) => index === self.findIndex((t) => t.id === item.id)
      );
  }

  public getProductType(
    projectId: number,
    layoutTypeId: number,
    categoryId: number,
    unitId: number,
    id: number,
  ): Observable<LayoutProductType> {
    const params: HttpParams = new HttpParams().set('unit_id', String(unitId)).append('only_image_versions', 'w260');

    return this.http
      .get<LayoutProductTypeResponse>(
        `${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_categories/${categoryId}/layout_product_types/${id}`,
        { params },
      )
      .pipe(
        map((res) => res.data),
        map((x: LayoutProductType) => plainToClass(LayoutProductType, x)),
        map((type: LayoutProductType) => {
          if (!type.grouped) {
            type.products?.forEach((product) => {
              product.exclusionProducts =
                product.dependableProducts?.filter((x) => x.ruleKind === ERuleKind.Exclusion) || [];
              product.forcingProducts =
                product.dependableProducts?.filter((x) => x.ruleKind === ERuleKind.Forcing) || [];
            });

            type.increaseProducts = this.getUniqueDependableProducts(
              type.products,
              ERuleKind.Increasing
            );
            type.decreaseProducts = this.getUniqueDependableProducts(
              type.products,
              ERuleKind.Decreasing
            );
            type.increaseTriggerProducts = this.getUniqueDependableProducts(
              type.products,
              ERuleKind.Increasing,
              EOptionRuleItemsKind.Triggerable
            );
            type.decreaseTriggerProducts = this.getUniqueDependableProducts(
              type.products,
              ERuleKind.Decreasing,
              EOptionRuleItemsKind.Triggerable
            );
          } else {
            type.groups?.forEach((group) => {
              group.products?.forEach((product) => {
                product.exclusionProducts =
                  product.dependableProducts?.filter((x) => x.ruleKind === ERuleKind.Exclusion) || [];
                product.forcingProducts =
                  product.dependableProducts?.filter((x) => x.ruleKind === ERuleKind.Forcing) || [];
              });
            });
          }
          return type;
        }),
      );
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Client-Products/paths/~1api~1v1~1projects~1%7Bproject_id%7D~1layout_types~1%7Blayout_type_id%7D~1layout_option_categories~1%7Blayout_option_category_id%7D~1layout_product_types~1%7Blayout_product_type_id%7D~1products~1%7Bid%7D/get
   */
  public getProductDetails(
    projectId: number,
    layoutTypeId: number,
    categoryId: number,
    productTypeId: number,
    productId: number,
    unitId?: number,
  ): Observable<ProductModel> {
    let params = new HttpParams();
    if (unitId) {
      params = params.append('unit_id', String(unitId));
    }
    return this.http
      .get<{
        data: ProductModel;
      }>(
        `${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_categories/${categoryId}/layout_product_types/${productTypeId}/products/${productId}`,
        { params },
      )
      .pipe(
        map((res) => res.data),
        map((product) => plainToClass(ProductModel, product)),
        map((pr: ProductModel) => {
          pr.exclusionProducts = pr.dependableProducts?.filter((x) => x.ruleKind === ERuleKind.Exclusion) || [];
          pr.forcingProducts = pr.dependableProducts?.filter((x) => x.ruleKind === ERuleKind.Forcing) || [];
          return pr;
        }),
      );
  }

  public getClientPackages(
    projectId: number,
    layoutTypeId: number,
    unitId: number,
  ): Observable<ClientPackageDataModel> {
    const params: HttpParams = new HttpParams().set('unit_id', String(unitId));
    return this.http
      .get<{
        data: { layout_option_packages: ClientPackageModel[] };
      }>(`${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_packages`, { params })
      .pipe(
        map((res) => res.data),
        map((packageData) => plainToClass(ClientPackageDataModel, packageData)),
        tap((packages) => {
          this.setPackages(packages);
        }),
      );
  }


  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/ClientLayout-Option-Package/paths/~1api~1v1~1projects~1%7Bproject_id%7D~1layout_types~1%7Blayout_type_id%7D~1layout_option_packages~1%7Bid%7D~1select/post
   */
  public selectPackage(projectId: number, layoutTypeId: number, packageId: number, unitId: number, action: 'select' | 'unselect' = 'select'): Observable<string> {
    const params: HttpParams = new HttpParams().set('unit_id', unitId.toString());
    return this.http
      .post(
        `${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_packages/${packageId}/${action}`,
        null,
        { params },
      )
      .pipe(map((res: any) => <string>res?.message));
  }

  public updateProductTypeItems(
    projectId: number,
    layoutTypeId: number,
    categoryId: number,
    typeId: number,
    productId: number,
    unitId: number,
    body: FormData,
  ): Observable<ResFreeTextProductModel> {
    const params: HttpParams = new HttpParams().set('unit_id', unitId.toString());
    return this.http
      .put<{
        data: ResFreeTextProductModel;
      }>(
        `${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_categories/${categoryId}/layout_product_types/${typeId}/products/${productId}`,
        body,
        { params },
      )
      .pipe(
        map((res) => res.data),
        map((product: ResFreeTextProductModel) => plainToClass(ResFreeTextProductModel, product)),
      );
  }

  public resetWishlistItems(projectId: number, unitId: number): Observable<WishlistModel> {
    return this.http
      .post<LayoutProductTypesResponse>(
        `${this.apiUrl}api/v1/client/projects/${projectId}/units/${unitId}/wishlist/reset_items`,
        null,
      )
      .pipe(
        map((res) => res.data),
        map((data) => plainToClass(WishlistModel, data)),
      );
  }

  /**
   * @see https://api.journeyapp.dev.scrij.com/api-docs#tag/Client-Layout-Option-Categories/paths/~1api~1v1~1projects~1{project_id}~1layout_types~1{layout_type_id}~1layout_option_categories~1standard_products/get
   */
  public getStandardProducts(
    projectId: number,
    layoutTypeId: number,
    unitId: number,
  ): Observable<OptionCategoryProduct[]> {
    const params: HttpParams = new HttpParams().set('unit_id', String(unitId)).append('only_image_versions', 'w260');
    return this.http
      .get<{
        data: { categories: OptionCategoryProduct[] };
      }>(
        `${this.apiUrl}api/v1/projects/${projectId}/layout_types/${layoutTypeId}/layout_option_categories/standard_products`,
        { params },
      )
      .pipe(
        map((res) => res.data.categories),
        map((data: OptionCategoryProduct[]) => plainToClass(OptionCategoryProduct, data)),
      );
  }

  /**
   * @see   https://api.journeyapp.dev.scrij.com/api-docs#tag/Profile/paths/~1api~1v1~1profile~1complete_digital_showroom_onboarding/put
   */
  public completeShowroomOnboarding(): Observable<any> {
    return this.http.put(`${this.apiUrl}api/v1/profile/complete_digital_showroom_onboarding`, null);
  }
}
